import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Page, PageBody, PageHeader, Panel, Toolbar } from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, SearchInput, Select } from '@/ui/Field';
import { Badge } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { api, errorMessage } from '@/lib/api/client';
import { useAtLeast, useAuth } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel, humanize, relativeTime, smartDate } from '@/lib/format';
import {
  fullName,
  grantableRoles,
  roleTone,
  sortStaff,
  validatePassword,
} from '@/features/system/staff';
import type { UserSummary } from '@/lib/api/types';
import { asText } from '@/lib/searchParams';

/**
 * Staff.
 *
 * The API enforces vertical-escalation guards on every write: you cannot mint
 * or promote a user above your own role, you cannot touch anyone senior to you,
 * you cannot deactivate yourself, you cannot demote yourself. The UI mirrors
 * every one of those so the operator can see *why* an option is missing — but
 * the server is the authority and its 403 message is always surfaced verbatim.
 */

type StaffFilter = 'all' | 'active' | 'inactive';

interface StaffSearch {
  q: string;
  view: StaffFilter;
}

export const Route = createFileRoute('/_app/system/staff')({
  staticData: { crumb: 'Staff' },
  validateSearch: (s: Record<string, unknown>): StaffSearch => ({
    q: asText(s.q, ''),
    view: s.view === 'active' || s.view === 'inactive' ? s.view : 'all',
  }),
  component: StaffScreen,
});

function StaffScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  // `users.write` has a manager floor — mirrored here only so the operator is
  // not offered a button the server would refuse.
  const canWrite = useAtLeast('manager');
  const [creating, setCreating] = useState(false);

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Open user' },
  ]);

  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
  });

  const rows = useMemo(() => {
    const term = search.q.trim().toLowerCase();
    return sortStaff(staff.data ?? []).filter((u) => {
      if (search.view === 'active' && !u.active) return false;
      if (search.view === 'inactive' && u.active) return false;
      if (!term) return true;
      return `${u.username} ${fullName(u)} ${u.role}`.toLowerCase().includes(term);
    });
  }, [staff.data, search.q, search.view]);

  const activeCount = (staff.data ?? []).filter((u) => u.active).length;

  function patch(next: Partial<StaffSearch>) {
    void navigate({ search: (prev: StaffSearch) => ({ ...prev, ...next }) });
  }

  const columns: Array<Column<UserSummary>> = [
    {
      id: 'name',
      header: 'Name',
      width: 'minmax(0, 1.3fr)',
      cell: (u) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className={u.active ? 'text-ink truncate' : 'text-ink-faint truncate'}>
            {fullName(u)}
          </span>
          {user && user.id === u.id ? (
            <span className="text-2xs text-ink-faint shrink-0">(you)</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'username',
      header: 'Username',
      width: 'minmax(0, 1fr)',
      mono: true,
      cell: (u) => <span className="text-ink-muted truncate">{u.username}</span>,
    },
    {
      id: 'role',
      header: 'Role',
      width: '7rem',
      cell: (u) => <Badge tone={roleTone(u.role)}>{humanize(u.role)}</Badge>,
    },
    {
      id: 'active',
      header: 'Account',
      width: '6.5rem',
      cell: (u) =>
        u.active ? (
          <Badge tone="positive" dot>
            Active
          </Badge>
        ) : (
          <Badge tone="neutral">Deactivated</Badge>
        ),
    },
    {
      id: 'lastLogin',
      header: 'Last sign-in',
      width: '8rem',
      mono: true,
      hideBelow: 'md',
      cell: (u) => (
        <span className="text-ink-muted" title={u.lastLoginAt ?? 'never'}>
          {u.lastLoginAt ? relativeTime(u.lastLoginAt) : 'never'}
        </span>
      ),
    },
    {
      id: 'created',
      header: 'Added',
      width: '6.5rem',
      mono: true,
      hideBelow: 'lg',
      cell: (u) => <span className="text-ink-faint">{smartDate(u.createdAt)}</span>,
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Staff"
        subtitle={`${countLabel(activeCount, 'active account')} of ${staff.data?.length ?? 0}`}
        actions={
          canWrite ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus className="size-3.5" />}
              onClick={() => setCreating(true)}
            >
              New user
            </Button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          size="sm"
          value={search.q}
          onValueChange={(q) => patch({ q })}
          placeholder="Filter by name, username or role…"
          className="w-72"
        />
        <ChipGroup<StaffFilter>
          value={search.view}
          onChange={(view) => patch({ view })}
          options={[
            { value: 'all', label: 'All', count: staff.data?.length ?? 0 },
            { value: 'active', label: 'Active', count: activeCount },
            {
              value: 'inactive',
              label: 'Deactivated',
              count: (staff.data?.length ?? 0) - activeCount,
            },
          ]}
        />
      </Toolbar>

      <PageBody className="flex min-h-0 flex-col gap-3">
        {!canWrite ? (
          <Callout tone="info" title="Read-only">
            Creating and editing staff needs the{' '}
            <code className="font-mono">users.write</code> capability, which starts at
            manager.
          </Callout>
        ) : null}

        <Panel title="Accounts" bleed className="min-h-0 flex-1">
          <DataTable
            rows={staff.isLoading ? undefined : rows}
            columns={columns}
            rowKey={(u) => u.id}
            loading={staff.isLoading}
            error={staff.error}
            onRetry={() => void staff.refetch()}
            onRowOpen={(u) =>
              void navigate({ to: '/system/staff/$id', params: { id: String(u.id) } })
            }
            rowTone={(u) => (u.active ? null : 'info')}
            empty={{
              icon: <Users />,
              title: search.q ? 'No matching staff' : 'No staff',
              description: search.q ? 'Clear the filter to see everyone.' : undefined,
            }}
            footer={<span>{countLabel(rows.length, 'account')}</span>}
          />
        </Panel>
      </PageBody>

      <NewUserDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

/* ── Create ───────────────────────────────────────────────────────────────── */

export function NewUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const roles = grantableRoles(user?.role);
  const blockedRoles = ['cashier', 'supervisor', 'manager', 'admin'].filter(
    (r) => !roles.includes(r as (typeof roles)[number])
  );

  const [form, setForm] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'cashier',
  });

  function reset() {
    setForm({ username: '', password: '', firstName: '', lastName: '', role: 'cashier' });
  }

  // POST /users echoes a narrower row than GET /users — no lastLoginAt/createdAt.
  type CreatedUser = Pick<
    UserSummary,
    'id' | 'username' | 'firstName' | 'lastName' | 'role' | 'active'
  >;
  const create = useMutation({
    mutationFn: () => api.post<CreatedUser>('/users', form),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
      reset();
      toast.success(`${fullName(created)} added`, {
        description: `Signs in as ${created.username}. Set a register PIN from their profile.`,
      });
    },
    // The server re-enforces the escalation guard; quote it verbatim.
    onError: (error) =>
      toast.error('Could not create user', { description: errorMessage(error) }),
  });

  const passwordError = validatePassword(form.password);
  const valid =
    form.username.trim().length > 0 &&
    form.firstName.trim().length > 0 &&
    form.password.length >= 4 &&
    !passwordError;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="New user"
      description="Creates a sign-in account. A register PIN is set separately on the user's profile."
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create user
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name" required>
          <Input
            autoFocus
            value={form.firstName}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, firstName: value }));
            }}
          />
        </Field>
        <Field label="Last name">
          <Input
            value={form.lastName}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, lastName: value }));
            }}
          />
        </Field>
        <Field label="Username" required hint="Used to sign in to the console.">
          <Input
            mono
            value={form.username}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, username: value }));
            }}
          />
        </Field>
        <Field
          label="Password"
          required
          error={form.password ? passwordError : null}
          hint="At least 4 characters."
        >
          <Input
            type="password"
            value={form.password}
            invalid={!!(form.password && passwordError)}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, password: value }));
            }}
          />
        </Field>
        <Field
          label="Role"
          className="sm:col-span-2"
          hint={
            blockedRoles.length > 0
              ? `You cannot create a user above your own role, so ${blockedRoles.join(' and ')} are unavailable.`
              : undefined
          }
        >
          <Select
            value={form.role}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, role: value }));
            }}
          >
            {roles.map((role) => (
              <option key={role} value={role}>
                {humanize(role)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
